<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import {
    LayoutDashboard,
    Inbox,
    Rocket,
    CalendarDays,
    Folder,
    Users,
    Settings,
    LogOut,
    Plus
  } from 'lucide-svelte';
  import { route } from '$lib/state/route.svelte';
  import { istDay } from '$lib/utils/format';
  import { session } from '$lib/state/session.svelte';
  import { ticketsApi } from '$lib/api/tickets';
  import Avatar from '$lib/components/ui/Avatar.svelte';
  import ProjectModal from '$lib/components/admin/ProjectModal.svelte';

  const {
    onLogout,
    open = false,
    onNavigate
  }: { onLogout: () => void; open?: boolean; onNavigate?: () => void } = $props();

  const r = $derived(route.current);
  const admin = $derived(session.isAdmin);
  const me = $derived(session.me);

  let showNewProject = $state(false);

  // Every nav action funnels through here so the mobile drawer (Shell's
  // `mobileNavOpen`) closes itself after a tap — desktop no-ops since
  // `onNavigate` is only wired up to close state there.
  function go(fn: () => void) {
    fn();
    onNavigate?.();
  }

  // Ticket-inbox badge — admin only (members never see the inbox).
  const inboxQuery = createQuery(() => ({
    queryKey: ['tickets-inbox-count'],
    queryFn: () => ticketsApi.inbox(),
    enabled: admin
  }));
  const inboxCount = $derived(inboxQuery.data?.count ?? 0);

  function openProject(id: number) {
    go(() => route.push({ tab: 'project', projectId: id }));
  }
</script>

<div class="sidebar {open ? 'open' : ''}">
  <div class="brand">
    <div class="mark">
      <svg width="19" height="19" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="8.5" fill="none" stroke="var(--lime)" stroke-width="3" />
        <circle cx="12" cy="12" r="2.4" fill="var(--lime)" />
      </svg>
    </div>
    <div>
      <div class="bt">Genba</div>
      <div class="bs">Development console</div>
    </div>
  </div>

  {#if admin}
    <div class="side-label">Manage</div>
    <button type="button" class="nav-item {r.tab === 'overview' ? 'active' : ''}" onclick={() => go(() => route.push({ tab: 'overview' }))}>
      <LayoutDashboard size={18} /><span class="nt">Projects</span>
    </button>
    <button type="button" class="nav-item {r.tab === 'inbox' ? 'active' : ''}" onclick={() => go(() => route.push({ tab: 'inbox' }))}>
      <Inbox size={18} /><span class="nt">Ticket inbox</span>
      {#if inboxCount > 0}<span class="nav-badge">{inboxCount}</span>{/if}
    </button>
    <button type="button" class="nav-item {r.tab === 'deploys' ? 'active' : ''}" onclick={() => go(() => route.push({ tab: 'deploys' }))}>
      <Rocket size={18} /><span class="nt">Deploys</span>
    </button>
    <!-- Always enters on today; the arrows on the page walk from there (and
         the day lives in the hash, so the nav entry stays lit on any day). -->
    <button type="button" class="nav-item {r.tab === 'daily' ? 'active' : ''}" onclick={() => go(() => route.push({ tab: 'daily', date: istDay() }))}>
      <CalendarDays size={18} /><span class="nt">Daily activity</span>
    </button>
    <button type="button" class="nav-item {r.tab === 'users' ? 'active' : ''}" onclick={() => go(() => route.push({ tab: 'users' }))}>
      <Users size={18} /><span class="nt">Users</span>
    </button>
    <div class="side-label">Projects</div>
    {#each me?.projects ?? [] as p (p.id)}
      <button
        type="button"
        class="nav-item {r.tab === 'project' && r.projectId === p.id ? 'active' : ''}"
        onclick={() => openProject(p.id)}
      >
        <Folder size={18} /><span class="nt">{p.name}</span>
      </button>
    {/each}
  {:else}
    <div class="side-label" style="display:flex;align-items:center;justify-content:space-between;padding-right:6px">
      <span>{(me?.projects.length ?? 0) > 1 ? 'Your projects' : 'Your project'}</span>
      <button
        type="button"
        class="icon-btn"
        style="width:22px;height:22px;border-radius:6px"
        onclick={() => go(() => (showNewProject = true))}
        aria-label="New project"
        title="New project"
      >
        <Plus size={14} />
      </button>
    </div>
    {#each me?.projects ?? [] as p (p.id)}
      <button
        type="button"
        class="nav-item {r.tab === 'project' && r.projectId === p.id ? 'active' : ''}"
        onclick={() => openProject(p.id)}
      >
        <Folder size={18} /><span class="nt">{p.name}</span>
      </button>
    {:else}
      <div style="padding:8px 10px;font-size:12px;color:var(--t3)">No projects granted.</div>
    {/each}
  {/if}

  <div class="side-foot">
    {#if me}
      <div class="usercard">
        <Avatar id={me.user.id} name={me.user.name || me.user.email} size={30} />
        <div style="min-width:0">
          <div class="un">{me.user.name || me.user.email}</div>
          <div class="ur">{admin ? 'Admin · founder' : 'Developer'}</div>
        </div>
      </div>
    {/if}
    <button type="button" class="nav-item {r.tab === 'settings' ? 'active' : ''}" onclick={() => go(() => route.push({ tab: 'settings' }))}>
      <Settings size={18} /><span class="nt">Settings</span>
    </button>
    <button type="button" class="nav-item" onclick={() => go(onLogout)}>
      <LogOut size={18} /><span class="nt">Log out</span>
    </button>
  </div>
</div>

{#if showNewProject}
  <ProjectModal project={null} members={[]} memberMode onClose={() => (showNewProject = false)} />
{/if}
